import type { ComponentProps, ComponentPropsWithoutRef, ReactNode, Ref } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema, type Options as SanitizeSchema } from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

import { cn } from '@/lib/utils';

type ReactMarkdownPlugins = NonNullable<ComponentProps<typeof ReactMarkdown>['rehypePlugins']>;

interface MarkdownContentProps {
  /** Raw markdown source. May contain inline `<mark>` tags for highlight injection. */
  readonly content: string;
  /** Outer wrapper class. Block defaults include typography spacing; pass extra layout classes here. */
  readonly className?: string;
  /**
   * When set, strips paragraph wrappers and renders inline. Use for short
   * excerpts / single-line snippets that should sit inside flex/grid rows.
   */
  readonly inline?: boolean;
  /** Ref attached to the first `<mark data-active="true">` element, if any. */
  readonly activeMarkRef?: Ref<HTMLElement>;
}

const blockClassName = cn(
  'text-sm leading-6 text-foreground break-words',
  '[&>*:first-child]:mt-0 [&>*:last-child]:mb-0',
  '[&_h1]:mt-4 [&_h1]:mb-2 [&_h1]:text-lg [&_h1]:font-semibold',
  '[&_h2]:mt-3 [&_h2]:mb-2 [&_h2]:text-base [&_h2]:font-semibold',
  '[&_h3]:mt-3 [&_h3]:mb-1.5 [&_h3]:text-sm [&_h3]:font-semibold',
  '[&_h4]:mt-2 [&_h4]:mb-1 [&_h4]:text-sm [&_h4]:font-semibold',
  '[&_p]:my-2',
  '[&_strong]:font-semibold [&_em]:italic',
  '[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5',
  '[&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5',
  '[&_li]:my-1 [&_li>p]:my-0',
  '[&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2',
  '[&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.85em]',
  '[&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-muted [&_pre]:p-3',
  '[&_pre>code]:bg-transparent [&_pre>code]:p-0 [&_pre>code]:text-xs',
  '[&_blockquote]:my-2 [&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground',
  '[&_hr]:my-4 [&_hr]:border-border',
  '[&_table]:my-2 [&_table]:w-full [&_table]:border-collapse [&_table]:text-xs',
  '[&_th]:border [&_th]:border-border [&_th]:bg-muted/50 [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_th]:font-semibold',
  '[&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1 [&_td]:align-top',
);

const highlightMarkClass = 'ca-citation-mark px-0.5';

// Sanitization schema layered on top of `rehype-raw`. Markdown content can
// originate from backend excerpts and (in chat) user-authored input. We allow
// the safe GFM-rendered tag set plus our highlight `<mark data-active>` and
// strip anything else (script, style, event handlers, etc.) to prevent XSS.
// Note: hast-util-sanitize uses HAST property names, so `data-active` is
// expressed as `dataActive` and `class` as `className`.
const sanitizeSchema: SanitizeSchema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames ?? []), 'mark'],
  attributes: {
    ...defaultSchema.attributes,
    mark: ['className', 'dataActive'],
  },
};

const remarkPlugins: ReactMarkdownPlugins = [remarkGfm];
// Order matters: parse raw HTML first, then sanitize the resulting tree.
const rehypePlugins: ReactMarkdownPlugins = [rehypeRaw, [rehypeSanitize, sanitizeSchema]];

type MarkProps = ComponentPropsWithoutRef<'mark'> & {
  // rehype-raw forwards arbitrary data-* attributes through `extraProps`.
  readonly 'data-active'?: string;
};

function buildComponents(
  activeMarkRef: Ref<HTMLElement> | undefined,
  inline: boolean,
): Components {
  const components: Components = {
    mark: ({ 'data-active': dataActive, className, ...rest }: MarkProps) => {
      const mergedClass = cn(highlightMarkClass, className);
      if (dataActive === 'true' && activeMarkRef !== undefined) {
        return <mark ref={activeMarkRef} className={mergedClass} {...rest} />;
      }
      return <mark className={mergedClass} {...rest} />;
    },
  };
  if (inline) {
    // Drop the surrounding paragraph wrapper so the excerpt can sit inline
    // inside flex/grid layouts (line-clamp, breadcrumbs, etc.).
    components.p = ({ children }: { readonly children?: ReactNode }) => <>{children}</>;
  }
  return components;
}

export function MarkdownContent({
  content,
  className,
  inline = false,
  activeMarkRef,
}: MarkdownContentProps) {
  const components = buildComponents(activeMarkRef, inline);
  if (inline) {
    return (
      <span className={cn('inline [&_strong]:font-semibold [&_em]:italic', className)}>
        <ReactMarkdown
          remarkPlugins={remarkPlugins}
          rehypePlugins={rehypePlugins}
          components={components}
        >
          {content}
        </ReactMarkdown>
      </span>
    );
  }
  return (
    <div className={cn(blockClassName, className)}>
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        rehypePlugins={rehypePlugins}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
